import { AlertTriangle, ArrowUpDown, Clock, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { usePendingApprovals } from '@/features/approvals';
import { usePermissions } from '@/features/auth';
import { HistoryButton } from '@/features/audit-log';
import { useBrandDetails, useBrands } from '@/features/brands';
import { ApiError } from '@/shared/api/api-error';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';
import { ErrorState } from '@/shared/components/error-state';
import { SearchableSelect, type SearchableOption } from '@/shared/components/searchable-select';
import { StatusBadge } from '@/shared/components/status-badge';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { formatCurrency, formatDateTime, formatPercent } from '@/shared/lib/format';

import {
  useFxRates,
  usePaymentChannels,
  usePricePreview,
  useSetPaymentChannelStatus,
} from '../api/pricing.queries';
import type { PaymentChannel } from '../types/pricing.types';
import { ChannelDeleteDialog } from './channel-delete-dialog';
import { ChannelEditDialog } from './channel-edit-dialog';
import { ChannelOrderDialog } from './channel-order-dialog';
import { FxHistoryDialog } from './fx-history-dialog';

/**
 * The "Payment channels" tab label, with a count of fee changes waiting for
 * approval so the waiting action is visible before the tab is opened.
 */
export function ChannelsTabLabel({ countryId }: { countryId: number }) {
  const channels = usePaymentChannels(countryId);
  const waiting = (channels.data ?? []).filter((c) => c.pendingFeesApprovalId != null).length;

  return (
    <>
      Payment channels
      {waiting > 0 && (
        <span
          className="bg-status-info-bg text-status-info border-status-info-border ms-1.5 inline-flex min-w-5 items-center justify-center rounded-full border px-1 text-[11px] font-medium tabular-nums"
          aria-label={`${waiting} fee ${waiting === 1 ? 'change' : 'changes'} waiting for approval`}
          title="Fee changes waiting for approval"
        >
          {waiting}
        </span>
      )}
    </>
  );
}

/**
 * The "Exchange rates" tab label, with a warning count of rates that are stale
 * or missing (prices that depend on them cannot be calculated) and a count of
 * manual rates waiting for approval for this country's currencies.
 */
export function FxTabLabel({ countryId }: { countryId: number }) {
  const fx = useFxRates(countryId);
  const { can } = usePermissions();
  const pending = usePendingApprovals(can('approvals.read'));
  const rates = fx.data ?? [];
  const currencies = new Set(rates.map((r) => r.currencyCode));
  const waiting = (pending.data ?? []).filter(
    (item) => item.approval.type === 'FxRateOverride' && currencies.has(item.approval.entityId),
  ).length;
  const stale = rates.filter((r) => r.availability === 'Stale').length;
  const missing = rates.filter((r) => r.availability === 'Missing').length;
  const problems = stale + missing;
  const description = [
    stale > 0 ? `${stale} stale` : null,
    missing > 0 ? `${missing} missing` : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      Exchange rates
      {problems > 0 && (
        <span
          className="bg-status-warning-bg text-status-warning border-status-warning-border ms-1.5 inline-flex items-center gap-1 rounded-full border px-1.5 text-[11px] font-medium tabular-nums"
          aria-label={`Exchange rates need attention: ${description}`}
          title={`Exchange rates need attention: ${description}`}
        >
          <AlertTriangle className="size-3" aria-hidden="true" />
          {problems}
        </span>
      )}
      {waiting > 0 && (
        <span
          className="bg-status-info-bg text-status-info border-status-info-border ms-1.5 inline-flex items-center gap-1 rounded-full border px-1.5 text-[11px] font-medium tabular-nums"
          aria-label={`${waiting} manual ${waiting === 1 ? 'rate' : 'rates'} waiting for approval`}
          title="Manual rates waiting for approval"
        >
          <Clock className="size-3" aria-hidden="true" />
          {waiting}
        </span>
      )}
    </>
  );
}

/**
 * Payment channels of a country, in checkout order; switching one off needs a
 * reason. Names are edited directly; the position only through "Checkout
 * order" (like the brand and denomination orders); fee changes go to an Admin
 * for approval.
 */
export function ChannelsPanel({
  countryId,
  countryName,
}: {
  countryId: number;
  countryName: string;
}) {
  const channels = usePaymentChannels(countryId);
  const { can } = usePermissions();
  const [turningOff, setTurningOff] = useState<PaymentChannel | null>(null);
  const [editing, setEditing] = useState<PaymentChannel | null>(null);
  const [ordering, setOrdering] = useState(false);
  const [deleting, setDeleting] = useState<PaymentChannel | null>(null);
  const [reason, setReason] = useState('');
  const setStatus = useSetPaymentChannelStatus(countryId);
  const canManage = can('pricing.config.manage');
  const canOpenApprovals = can('approvals.read');
  // Deleting is irreversible: Admin only (countries.manage), and only for an unused channel.
  const canDelete = can('countries.manage');
  const hasActions = canManage || canDelete;

  if (channels.isLoading) return <Skeleton className="h-40" />;
  if (channels.error)
    return <ErrorState error={channels.error} onRetry={() => void channels.refetch()} />;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <p className="text-muted-foreground">
          Customers see the channels that are on, in this order.
        </p>
        {canManage && (channels.data?.length ?? 0) > 1 && (
          <Button size="sm" variant="outline" onClick={() => setOrdering(true)}>
            <ArrowUpDown className="size-3.5" aria-hidden />
            Checkout order
          </Button>
        )}
      </div>
      <div className="border-border overflow-x-auto rounded-md border">
        <Table className="text-xs">
          <TableHeader>
            <TableRow>
              <TableHead className="w-16 text-end">Position</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>Gateway</TableHead>
              <TableHead className="text-end">Fee</TableHead>
              <TableHead>Status</TableHead>
              {hasActions && (
                <TableHead className="w-20">
                  <span className="sr-only">Actions</span>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(channels.data ?? []).map((channel) => (
              <TableRow key={channel.id}>
                {/* Changed with "Checkout order". */}
                <TableCell className="text-end tabular-nums">{channel.sortOrder}</TableCell>
                <TableCell>
                  <span className="font-medium">{channel.name}</span>
                  <code className="text-muted-foreground ms-1 font-mono text-[11px]">
                    {channel.code}
                  </code>
                  <HistoryButton
                    entityType="PaymentChannel"
                    entityId={String(channel.id)}
                    name={channel.name}
                  />
                </TableCell>
                <TableCell>{channel.gatewayCode ?? '—'}</TableCell>
                <TableCell className="text-end tabular-nums">
                  {channel.percentFee}%{channel.fixedFee ? ` + ${channel.fixedFee}` : ''}
                  {channel.pendingFeesApprovalId != null && (
                    <span className="block">
                      <StatusBadge variant="info">Fee change waiting</StatusBadge>
                      {canOpenApprovals && (
                        <Link
                          to="/approvals?type=PaymentChannelFees"
                          className="ms-1 font-medium underline"
                          title="Fee changes are approved in the Approvals inbox"
                        >
                          Review
                        </Link>
                      )}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {canManage ? (
                    <span className="flex items-center gap-2">
                      <Switch
                        checked={channel.isActive}
                        disabled={setStatus.isPending}
                        aria-label={`${channel.name} at checkout`}
                        onCheckedChange={(on) => {
                          if (on) {
                            setStatus.mutate({
                              channelId: channel.id,
                              isActive: true,
                              reason: null,
                            });
                          } else {
                            // Switching off hides it from customers: ask why first.
                            setReason('');
                            setTurningOff(channel);
                          }
                        }}
                      />
                      <span className={channel.isActive ? undefined : 'text-muted-foreground'}>
                        {channel.isActive ? 'On' : 'Off'}
                      </span>
                    </span>
                  ) : (
                    <StatusBadge variant={channel.isActive ? 'success' : 'neutral'}>
                      {channel.isActive ? 'On' : 'Off'}
                    </StatusBadge>
                  )}
                </TableCell>
                {hasActions && (
                  <TableCell>
                    <span className="flex items-center justify-end gap-0.5">
                      {canManage && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="size-7"
                              onClick={() => setEditing(channel)}
                              aria-label={`Edit ${channel.name}`}
                            >
                              <Pencil className="size-3.5" aria-hidden />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit names and fees</TooltipContent>
                        </Tooltip>
                      )}
                      {/* Destructive, so in a menu away from the everyday actions. */}
                      {canDelete && (
                        <DropdownMenu>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="size-7"
                                  aria-label={`More actions for ${channel.name}`}
                                >
                                  <MoreHorizontal className="size-3.5" aria-hidden />
                                </Button>
                              </DropdownMenuTrigger>
                            </TooltipTrigger>
                            <TooltipContent>More actions</TooltipContent>
                          </Tooltip>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() => setDeleting(channel)}
                              className="gap-2 text-xs"
                            >
                              <Trash2 className="size-3.5" aria-hidden />
                              Delete channel…
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </span>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editing && (
        <ChannelEditDialog
          countryId={countryId}
          channel={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting && (
        <ChannelDeleteDialog
          countryId={countryId}
          countryName={countryName}
          channel={deleting}
          onClose={() => setDeleting(null)}
        />
      )}
      {ordering && (
        <ChannelOrderDialog
          countryId={countryId}
          countryName={countryName}
          onClose={() => setOrdering(false)}
        />
      )}

      <ConfirmDialog
        open={turningOff !== null}
        onOpenChange={(open) => !open && setTurningOff(null)}
        title={`Switch off ${turningOff?.name ?? 'this channel'}?`}
        description="Customers in this country can no longer pay through it. Orders already paid are unaffected."
        confirmLabel="Switch off"
        variant="destructive"
        isPending={setStatus.isPending}
        confirmDisabled={reason.trim() === ''}
        onConfirm={() => {
          if (!turningOff) return;
          setStatus.mutate(
            { channelId: turningOff.id, isActive: false, reason: reason.trim() },
            { onSuccess: () => setTurningOff(null) },
          );
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="channel-off-reason" className="text-xs">
            Reason <span className="text-muted-foreground font-normal">(required)</span>
          </Label>
          <Input
            id="channel-off-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={500}
            className="h-8 text-xs"
          />
          {setStatus.error instanceof ApiError && (
            <p className="text-destructive text-xs">{setStatus.error.message}</p>
          )}
        </div>
      </ConfirmDialog>
    </div>
  );
}

/**
 * Exchange rates pricing depends on, with their age against the country's
 * limit. Each opens its history, where a manual rate can be requested.
 */
export function FxPanel({ countryId }: { countryId: number }) {
  const fx = useFxRates(countryId);
  const { can } = usePermissions();
  const canOpenApprovals = can('approvals.read');
  const pending = usePendingApprovals(canOpenApprovals);
  const [open, setOpen] = useState<string | null>(null);
  /** The waiting manual-rate request of a currency, if any. */
  const waitingFor = (currencyCode: string) =>
    (pending.data ?? []).find(
      (item) => item.approval.type === 'FxRateOverride' && item.approval.entityId === currencyCode,
    )?.approval ?? null;
  if (fx.isLoading) return <Skeleton className="h-32" />;
  if (fx.error) return <ErrorState error={fx.error} onRetry={() => void fx.refetch()} />;

  return (
    <div className="border-border overflow-x-auto rounded-md border">
      <Table className="text-xs">
        <TableHeader>
          <TableRow>
            <TableHead>Currency</TableHead>
            <TableHead className="text-end">Rate</TableHead>
            <TableHead>Fetched</TableHead>
            <TableHead>State</TableHead>
            <TableHead>Used by</TableHead>
            <TableHead>
              <span className="sr-only">History</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(fx.data ?? []).map((rate) => {
            const waiting = waitingFor(rate.currencyCode);
            return (
              <TableRow key={rate.currencyCode}>
                <TableCell className="font-mono">{rate.currencyCode}</TableCell>
                <TableCell className="text-end tabular-nums">{rate.rate ?? '—'}</TableCell>
                <TableCell>
                  {rate.fetchedAtUtc ? formatDateTime(rate.fetchedAtUtc) : '—'}
                  {rate.ageHours !== null && (
                    <span className="text-muted-foreground"> ({rate.ageHours} h old)</span>
                  )}
                </TableCell>
                <TableCell>
                  <StatusBadge
                    variant={
                      rate.availability === 'Available'
                        ? 'success'
                        : rate.availability === 'Stale'
                          ? 'warning'
                          : 'danger'
                    }
                  >
                    {rate.availability === 'Stale'
                      ? `Stale (over ${rate.maximumAgeHours ?? '?'} h)`
                      : rate.availability}
                  </StatusBadge>
                  {rate.isOverride && (
                    <span className="block">
                      <StatusBadge variant="warning">
                        Manual until {formatDateTime(rate.overrideExpiresAtUtc ?? null)}
                      </StatusBadge>
                    </span>
                  )}
                  {waiting && (
                    <span className="mt-1 block">
                      <StatusBadge variant="info">Manual rate waiting</StatusBadge>
                      <Link
                        to="/approvals?type=FxRateOverride"
                        className="ms-1 font-medium underline"
                        title="Manual rates are approved in the Approvals inbox"
                      >
                        Review
                      </Link>
                      <span className="text-muted-foreground block">{waiting.summary}</span>
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {rate.usedBy.length > 0 ? rate.usedBy.join(', ') : 'Customer prices'}
                </TableCell>
                <TableCell className="text-end">
                  {rate.currencyCode !== 'USD' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7"
                      onClick={() => setOpen(rate.currencyCode)}
                      aria-label={`History of ${rate.currencyCode}`}
                    >
                      History
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {open && (
        <FxHistoryDialog countryId={countryId} currencyCode={open} onClose={() => setOpen(null)} />
      )}
    </div>
  );
}

/**
 * What a customer pays for one item through each active channel — through the
 * checkout path — and with the draft parameters for comparison.
 */
export function PreviewPanel({ countryId, hasDraft }: { countryId: number; hasDraft: boolean }) {
  const [brandId, setBrandId] = useState<number | null>(null);
  const [brandSearch, setBrandSearch] = useState('');
  const [denominationId, setDenominationId] = useState<number | null>(null);
  const [includeDraft, setIncludeDraft] = useState(hasDraft);
  const search = useDebouncedValue(brandSearch.trim());
  const brands = useBrands({
    page: 1,
    pageSize: 50,
    search: search || undefined,
    countryId,
    sort: 'name',
    direction: 'asc',
  });
  const details = useBrandDetails(brandId ?? undefined);
  const preview = usePricePreview(countryId, denominationId, includeDraft);
  const data = preview.data;
  const showDraft = data?.draftVersion !== null && data?.draftVersion !== undefined;
  const showCost = data?.lines.some((line) => line.cost !== null) ?? false;

  const brandOptions: SearchableOption[] = (brands.data?.items ?? []).map((brand) => ({
    value: String(brand.id),
    label: brand.isActive ? brand.name : `${brand.name} (inactive)`,
  }));
  const regions = details.data?.regions ?? [];
  const activeDenominations = (details.data?.denominations ?? []).filter((d) => d.isActive);
  // Region is part of the label only when the brand sells in more than one store/region.
  const multiRegion = new Set(activeDenominations.map((d) => d.regionId)).size > 1;
  const denominationOptions: SearchableOption[] = activeDenominations.map((denomination) => {
    const region = regions.find((r) => r.id === denomination.regionId);
    const regionName = region?.name ?? region?.code ?? `Region ${denomination.regionId}`;
    return {
      value: String(denomination.id),
      label: multiRegion ? `${regionName} · ${denomination.name}` : denomination.name,
    };
  });

  return (
    <div className="flex flex-col gap-3 text-xs">
      <div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
        <div className="flex min-w-0 flex-col gap-1">
          <Label htmlFor="preview-brand" className="text-xs">
            Brand
          </Label>
          <SearchableSelect
            id="preview-brand"
            label="Brand"
            value={brandId === null ? null : String(brandId)}
            selectedLabel={details.data?.brand.name}
            options={brandOptions}
            onChange={(id) => {
              setBrandId(Number(id));
              setDenominationId(null);
            }}
            placeholder="Search and select a brand"
            searchPlaceholder="Type a brand name…"
            emptyText="No brand matches."
            search={brandSearch}
            onSearchChange={setBrandSearch}
            loading={brands.isFetching && brandOptions.length === 0}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <Label htmlFor="preview-denomination" className="text-xs">
            Denomination
          </Label>
          <SearchableSelect
            id="preview-denomination"
            label="Denomination"
            value={denominationId === null ? null : String(denominationId)}
            options={denominationOptions}
            onChange={(id) => setDenominationId(Number(id))}
            placeholder={
              brandId !== null && details.isLoading
                ? 'Loading denominations…'
                : 'Search and select a denomination'
            }
            searchPlaceholder="Type a denomination…"
            emptyText="No denomination matches."
            disabled={brandId === null || details.isLoading}
          />
        </div>
        <label className="flex items-center gap-1.5 pb-2">
          <Checkbox
            checked={includeDraft}
            onCheckedChange={(value) => setIncludeDraft(value === true)}
          />
          Compare with the draft
        </label>
      </div>
      {details.error && <ErrorState error={details.error} onRetry={() => void details.refetch()} />}

      {preview.isLoading && <Skeleton className="h-24" />}
      {preview.error && <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />}
      {data && (
        <div className="border-border overflow-x-auto rounded-md border">
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Channel</TableHead>
                <TableHead className="text-end">Price (v{data.activeVersion ?? '—'})</TableHead>
                {showDraft && (
                  <TableHead className="text-end">Draft price (v{data.draftVersion})</TableHead>
                )}
                <TableHead>Health</TableHead>
                {showCost && <TableHead className="text-end">Cost</TableHead>}
                {showCost && <TableHead className="text-end">Margin</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.lines.map((line) => (
                <Fragment key={line.channelCode}>
                  <TableRow className={line.failureMessage ? 'border-b-0' : undefined}>
                    <TableCell className="font-medium">{line.channelName}</TableCell>
                    <TableCell className="text-end tabular-nums">
                      {line.finalPrice === null ? (
                        <StatusBadge variant="danger">{line.status}</StatusBadge>
                      ) : (
                        formatCurrency(line.finalPrice, data.currency ?? 'USD')
                      )}
                    </TableCell>
                    {showDraft && (
                      <TableCell className="text-end tabular-nums">
                        {line.draftFinalPrice === null
                          ? '—'
                          : formatCurrency(line.draftFinalPrice, data.currency ?? 'USD')}
                      </TableCell>
                    )}
                    <TableCell>{line.priceHealth ?? '—'}</TableCell>
                    {showCost && (
                      <TableCell className="text-end tabular-nums">
                        {line.cost?.actualCost == null
                          ? '—'
                          : formatCurrency(line.cost.actualCost, data.currency ?? 'USD')}
                      </TableCell>
                    )}
                    {showCost && (
                      <TableCell className="text-end tabular-nums">
                        {formatPercent(line.cost?.realizedMargin)}
                        {showDraft && line.cost?.draftRealizedMargin != null && (
                          <span className="text-muted-foreground">
                            {' '}
                            → {formatPercent(line.cost.draftRealizedMargin)}
                          </span>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                  {line.failureMessage && (
                    <TableRow>
                      <TableCell
                        colSpan={3 + (showDraft ? 1 : 0) + (showCost ? 2 : 0)}
                        className="text-destructive pt-0 whitespace-normal"
                      >
                        {line.failureMessage}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
